import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, MessagesSquare, UserCheck, CalendarCheck, ShieldCheck } from "lucide-react";
import { fadeUp, stagger, scaleIn, viewport } from "./anim";

const bullets = [
  { icon: MessagesSquare, label: "Answers common questions instantly, day or night" },
  { icon: UserCheck, label: "Asks the right questions to qualify leads" },
  { icon: CalendarCheck, label: "Books appointments straight to your calendar" },
  { icon: ShieldCheck, label: "Tells people up front they're talking to AI" },
];

export const CaseStudy = () => {
  return (
    <section id="casestudy" data-testid="casestudy-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="blob w-[26rem] h-[26rem] right-0 top-10 animate-blob" style={{ background: "rgba(233,51,198,0.12)" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={stagger} className="max-w-2xl mb-12">
          <motion.div variants={fadeUp} className="font-mono-accent text-xs tracking-[0.25em] uppercase text-fuchsia-600 mb-4">
            See It In Action
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Don't take my word for it. <span className="text-gradient">Test it.</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={scaleIn}
          data-testid="casestudy-melavate-card"
          className="relative grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden glass-light"
        >
          {/* Neutral tech imagery — no people. Easy to swap anytime. */}
          <div
            data-testid="casestudy-image-placeholder"
            className="relative h-72 lg:h-auto min-h-[22rem] overflow-hidden"
          >
            <img
              src="https://images.unsplash.com/photo-1784316395212-29225817817f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Dual-monitor workstation where the AI assistants are built and tested"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg,rgba(233,51,198,0.30),rgba(139,92,246,0.22))", mixBlendMode: "multiply" }} />
          </div>

          <div className="p-8 sm:p-11">
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Before I build anything for a client, I build it for my own businesses first. MELavate
              Studios is my in-house AI studio, where I design and test the chatbots I offer clients.
              The assistant in the bottom corner of this page is one of them. Ask it a question, ask
              about services, or try to book a call. That's the same experience your customers would
              get.
            </p>
            <div className="mt-7 space-y-3">
              {bullets.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-fuchsia-50 border border-fuchsia-100 shrink-0">
                      <Icon size={17} className="text-fuchsia-600" />
                    </span>
                    <span className="text-slate-800 font-medium">{b.label}</span>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => window.dispatchEvent(new Event("open-elevatebot"))}
              data-testid="casestudy-try-chatbot"
              className="btn-glow mt-9 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold text-white"
              style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
            >
              Try the Chatbot → <MessageSquare size={17} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudy;
